import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, Mail, CheckCircle2, AlertTriangle, Clock, XCircle, SkipForward, Search, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS_ICONS = {
  sent:        { icon: Mail,          cls: "text-blue-400 bg-blue-400/10 border-blue-400/30" },
  delivered:   { icon: CheckCircle2,  cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
  bounced:     { icon: AlertTriangle, cls: "text-red-400 bg-red-400/10 border-red-400/30" },
  complained:  { icon: AlertTriangle, cls: "text-orange-400 bg-orange-400/10 border-orange-400/30" },
  failed:      { icon: XCircle,       cls: "text-red-400 bg-red-400/10 border-red-400/30" },
  skipped:     { icon: SkipForward,   cls: "text-zinc-400 bg-zinc-400/10 border-zinc-400/30" },
  delayed:     { icon: Clock,         cls: "text-amber-400 bg-amber-400/10 border-amber-400/30" },
  opened:      { icon: Eye,           cls: "text-sky-400 bg-sky-400/10 border-sky-400/30" },
  clicked:     { icon: Eye,           cls: "text-sky-400 bg-sky-400/10 border-sky-400/30" },
};

const EVENT_LABELS = {
  employee_admitted_welcome: "Admissão · Boas-vindas",
  employee_admitted_hr: "Admissão · RH",
  employee_terminated_hr: "Desligamento · RH",
  bulk_operation_summary: "Lote · Operador",
  bulk_operation_summary_approver: "Lote · Aprovador",
  application_received: "Candidatura recebida",
  stage_transition: "Transição de etapa",
  interview_scheduled: "Entrevista agendada",
  executive_snapshot: "Snapshot Executivo",
  clt_pj_report: "Relatório CLT×PJ",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso; }
};

export default function EmailEventsPanel({ companies = [], isMaster }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ counts: {}, total: 0 });
  const [webhookStatus, setWebhookStatus] = useState(null);

  const [filters, setFilters] = useState({
    company_id: "", event: "", status: "",
    date_from: "", date_to: "", search: "",
  });
  const [detail, setDetail] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/admin/emails/events/summary", { params });
      setSummary(data || { counts: {}, total: 0 });
    } catch (e) { /* silencioso */ }
  }, [filters.date_from, filters.date_to]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/emails/events", { params });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar eventos");
    } finally { setLoading(false); }
  }, [filters, page]);

  const loadWebhook = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/emails/webhook/status");
      setWebhookStatus(data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { loadSummary(); loadEvents(); loadWebhook(); }, [loadSummary, loadEvents, loadWebhook]);

  const handleFilter = (k) => (v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const StatusCard = ({ status, count }) => {
    const cfg = STATUS_ICONS[status] || { icon: Mail, cls: "text-zinc-400 bg-zinc-400/10 border-zinc-400/30" };
    const Icon = cfg.icon;
    return (
      <div className={`rounded-xl border px-4 py-3 ${cfg.cls}`}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em]">
          <Icon className="w-3.5 h-3.5" /> {status}
        </div>
        <div className="font-display text-2xl font-semibold mt-1">{count}</div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="email-events-panel">
      {/* Webhook banner */}
      {webhookStatus && !webhookStatus.configured && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3 flex items-start gap-3 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-amber-200">Webhook do Resend não configurado</div>
            <div className="text-amber-100/70 text-xs mt-1">
              Status "entregue/bounce/complained" só aparece quando o webhook está ativo. Configure a Secret
              <code className="mx-1 px-1 py-0.5 rounded bg-amber-400/20 text-amber-200">RESEND_WEBHOOK_SECRET</code>
              e aponte o Resend para
              <code className="mx-1 px-1 py-0.5 rounded bg-amber-400/20 text-amber-200">POST {webhookStatus.endpoint}</code>.
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="rounded-xl border border-[#C5A05940] bg-[#C5A05915] px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#C5A059]">Total</div>
          <div className="font-display text-2xl font-semibold mt-1 text-white">{summary.total}</div>
        </div>
        {["sent", "delivered", "failed", "bounced", "complained", "skipped"].map(s => (
          <StatusCard key={s} status={s} count={summary.counts?.[s] || 0} />
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">De</label>
            <Input type="date" value={filters.date_from} onChange={e => handleFilter("date_from")(e.target.value)} data-testid="emails-filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Até</label>
            <Input type="date" value={filters.date_to} onChange={e => handleFilter("date_to")(e.target.value)} data-testid="emails-filter-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Empresa</label>
            <Select value={filters.company_id || "all"} onValueChange={v => handleFilter("company_id")(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="emails-filter-company"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name || c.nome_fantasia || c.razao_social}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Evento</label>
            <Select value={filters.event || "all"} onValueChange={v => handleFilter("event")(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="emails-filter-event"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(EVENT_LABELS).map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Status</label>
            <Select value={filters.status || "all"} onValueChange={v => handleFilter("status")(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="emails-filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.keys(STATUS_ICONS).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Buscar</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#AAB4A8] absolute left-2 top-1/2 -translate-y-1/2" />
              <Input className="pl-7" placeholder="E-mail ou assunto" value={filters.search}
                     onChange={e => handleFilter("search")(e.target.value)} data-testid="emails-filter-search" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#AAB4A8]">{total} eventos encontrados {!isMaster && "(escopo da sua empresa)"}</div>
          <Button size="sm" variant="outline" onClick={() => { loadSummary(); loadEvents(); }}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data / hora</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Destinatário</TableHead>
              <TableHead>Assunto</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#C5A059]" /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#AAB4A8]">Nenhum evento no período/filtro atual.</TableCell></TableRow>
            ) : items.map((ev, i) => {
              const cfg = STATUS_ICONS[ev.status] || STATUS_ICONS.sent;
              const Icon = cfg.icon;
              return (
                <TableRow key={ev.id || i} data-testid="email-event-row">
                  <TableCell className="font-mono text-xs">{fmtDate(ev.at)}</TableCell>
                  <TableCell className="text-sm">{ev.company_name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <Badge variant="outline" className="font-normal">{EVENT_LABELS[ev.event] || ev.event}</Badge>
                  </TableCell>
                  <TableCell className="text-sm font-mono text-xs">{ev.to}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{ev.subject}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs border ${cfg.cls}`}>
                      <Icon className="w-3 h-3" /> {ev.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => setDetail(ev)} data-testid="email-event-detail-btn">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-[#AAB4A8]">
          <div>Página {page} de {totalPages}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detalhe do envio</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Evento" value={EVENT_LABELS[detail.event] || detail.event} />
                <Field label="Status" value={detail.status} />
                <Field label="Empresa" value={detail.company_name || "—"} />
                <Field label="Data/hora" value={fmtDate(detail.at)} />
                <Field label="Destinatário" value={detail.to} mono />
                <Field label="Resend ID" value={detail.resend_id || "—"} mono />
              </div>
              <Field label="Assunto" value={detail.subject} />
              {detail.error && <Field label="Erro" value={detail.error} className="text-red-300" />}
              {detail.webhook_history?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8] mb-2">Histórico de webhooks</div>
                  <div className="space-y-1 text-xs font-mono bg-[#0B140F] rounded border border-[#2C3A2F] p-3 max-h-48 overflow-y-auto">
                    {detail.webhook_history.map((h, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-[#C5A059]">{h.event}</span>
                        <span className="text-[#AAB4A8]">{fmtDate(h.at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.meta && Object.keys(detail.meta).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8] mb-2">Meta</div>
                  <pre className="text-xs font-mono bg-[#0B140F] rounded border border-[#2C3A2F] p-3 overflow-x-auto">
{JSON.stringify(detail.meta, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, mono, className = "" }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">{label}</div>
    <div className={`text-sm ${mono ? "font-mono text-xs" : ""} ${className}`}>{value || "—"}</div>
  </div>
);
